import styles from './contract-sale-dialog.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { Updater, useImmer } from 'use-immer';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { GetCurrentCompany } from '../customer-context/customer-context';
import RightsWrapper from '../rights-wrapper/rights-wrapper';
import {
  Checkbox, ContractOrderTypesEnum, IContractStateType, CreateHeaders, currencyHeader, Dialog, getAllSaleChanges, GrayOutlinedButton, HeaderSizeEnum, Icon, isNewLicense, ILicenseAccessChange,
  IOrderItem, PrimaryButton, SalePriceRenderer, Step, StepLabel, Stepper, TableHeader, TextField, textHeader, useSingleContractDispatch, LicenseTypeEnum, DialogTitle,
  DialogContent, DialogActions, Divider, DatePicker, Autocomplete,
  ILicenseAccessChangeOrder
} from '@frontend-workspace/react-shared-lib';
import { ILicenseAccessResponse, IUserAccesses } from '../_models/license-access-response';
import WebkuaTable from '../webkua-table/webkua-table';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';


/* eslint-disable-next-line */
export interface ContractSaleDialogProps {
  contract: IContractStateType,
  onClose: () => void,
  companyNumber: string,
  onNewSale: (licenseAccessOrder: ILicenseAccessChangeOrder) => void
}

export function ContractSaleDialog(props: ContractSaleDialogProps) {
  const [licenseAccessOrder, updateLicenseAccessOrder] = useImmer<ILicenseAccessChangeOrder>({ changedByCustomer: false, accessChanges: [] });
  const [activeStep, setActiveStep] = useState<number>(0);
  const { t } = useTranslation();
  const steps = [t("contractSale.approval"), t("contractSale.grantLicenseAccess")];

  const handleGiveLicenseClick = () => activeStep + 1;
  const handleSale = () => props.onNewSale(licenseAccessOrder);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (activeStep === 1) {
      handleSale();
      props.onClose();
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Dialog open={true} maxWidth={"xl"} fullWidth>
      <DialogTitle>{t("contractSale.newSale")}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} className='mb-5'>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        {activeStep === 0 && <LicensesPage
          onClose={props.onClose}
          contract={props.contract}
          onGiveLicenseAccessClick={handleGiveLicenseClick}
        />}
        {activeStep === 1 && <LicenseAccessPage
          contract={props.contract}
          onNewSale={props.onNewSale}
          onClose={props.onClose}
          licenseAccessOrder={licenseAccessOrder}
          updateLicenseAccessOrder={updateLicenseAccessOrder}
        />}
      </DialogContent>
      <DialogActions>
        <div className="d-flex flex-wrap">
          <GrayOutlinedButton
            disabled={activeStep === 0}
            onClick={handleBack}
            size='large'
            className='minButtonWidth'
            startIcon={<Icon>arrow_back</Icon>}>
            {t("standard.back")}
          </GrayOutlinedButton>
          <Divider orientation="vertical" variant="middle" flexItem className='mx-4' />
          <GrayOutlinedButton size='large' className='minButtonWidth me-3' onClick={props.onClose}>
            {t("standard.cancel")}
          </GrayOutlinedButton>
          <RightsWrapper>
            <PrimaryButton size='large' variant='contained' className='minButtonWidth mt-3 mt-sm-0' onClick={handleNext}>
              {activeStep === steps.length - 1 ? t("contractSale.makeSale") : t("standard.next")}
            </PrimaryButton>
          </RightsWrapper>
        </div>
      </DialogActions>
    </Dialog>
  );
}

function LicensesPage(props: {
  contract: IContractStateType,
  onClose: () => void,
  onGiveLicenseAccessClick: () => void
}) {
  const dispatch = useSingleContractDispatch();
  const contract = props.contract;
  const allChanges: IOrderItem[] = getAllSaleChanges(contract);
  const { data: reasonsForChange } = useGetRequestNewHook(`orders/reasons`, []);
  const { t } = useTranslation();

  const priceRenderer = (params: ValueFormatterParams) => SalePriceRenderer(props.contract.contractInfo, params.data, props.contract.newSaleDate);
  const licenseNumberRenderer = (params: ValueFormatterParams) => isNewLicense(params.value) ? t("contractSale.newLicense") : params.value;
  const handleSaleDateChange = (newDate: Date | null) => dispatch({ type: "SALE_ACTIVATION_DATE_CHANGE", newDate: newDate });

  const headers = CreateHeaders([
    textHeader("productName", t("standard.product"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.SMALL, { renderer: licenseNumberRenderer }),
    textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.SMALL, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("recurringPrice", t("standard.price"), HeaderSizeEnum.MEDIUM, {
      renderer: priceRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]);

  return <>
    <div className='mb-3'>
      <DatePicker
        inputFormat="dd.MM.yyyy"
        label={t("standard.executionDate")}
        value={contract.newSaleDate}
        onChange={handleSaleDateChange}
      />
    </div>
    <div className='mb-3'>
      <WebkuaTable
        headers={headers}
        data={allChanges}
      />
    </div>
    <Row>
      <Col xs="3">
        <Autocomplete
          value={contract.reasonForSale}
          onChange={(e, newValue) => dispatch({ type: "SALE_REASON_CHANGE", newValue: newValue })}
          options={reasonsForChange}
          getOptionLabel={(option) => t(`reasonsForSale.${option.name}`)}
          renderInput={(params: any) => <TextField {...params} label={t("standard.reasonForSale")} fullWidth />}
        />
      </Col>
      <Col>
        <TextField
          onBlur={(e) => dispatch({ type: "SALE_MESSAGE_CHANGE", newValue: e.target.value })}
          label={t("contractSale.internalMessage")}
          fullWidth
        />

      </Col>
    </Row>

  </>
}

function LicenseAccessPage(props: {
  onNewSale: (licenseAccessOrder: ILicenseAccessChangeOrder) => void,
  onClose: () => void,
  contract: IContractStateType,
  licenseAccessOrder: ILicenseAccessChangeOrder,
  updateLicenseAccessOrder: Updater<ILicenseAccessChangeOrder>
}) {
  const makeRequest = useMakeRequestHook();
  const companyNumber = GetCurrentCompany()?.number;
  const { t } = useTranslation();

  const changesLicenseNumbers = props.contract.orderItems.filter((o: any) =>
    !o.isExisting &&
    !o.isModule &&
    o.itemType !== LicenseTypeEnum.ONE_TIME &&
    (o.type === ContractOrderTypesEnum.AddSeats || o.type === ContractOrderTypesEnum.AddProductsToContract))
    .map((o: any) => {
      return {
        vismaId: o.vismaContractItemId,
        licenseNumber: o.licenseNumber
      };
    });

  const [tableHeaders, setTableHeaders] = useState<TableHeader[]>([]);
  const [licenseNumbers, setLicenseNumbers] = useState<Array<number>>([]);
  const [initialAccesses, setInitialAccesses] = useState<IUserAccesses[]>([]);

  const handleAccessChange = (licenseNumber: number, newValue: boolean, email: string) => {
    props.updateLicenseAccessOrder(draft => {
      const createAccessChange = () => {
        const accessChange: ILicenseAccessChange = {
          giveAccess: newValue,
          licenseNumber: licenseNumber,
          email: email,
          isCoordinator: false,
        }
        draft.accessChanges.push(accessChange);
      };
      const existingLicenseAccessChange = draft.accessChanges.find(u => u.email === email && u.licenseNumber === licenseNumber);
      existingLicenseAccessChange ? existingLicenseAccessChange.giveAccess = newValue : createAccessChange();

      return draft;
    });
  };

  const checkboxRenderer = (params: ValueFormatterParams) => {
    const user = params.data;
    const licenseNumber = params.colDef.headerName || "";
    const changesOnLicense = props.licenseAccessOrder.accessChanges.find(u => u.email === user.account && u.licenseNumber === Number(licenseNumber));
    const value = changesOnLicense ? changesOnLicense.giveAccess : user[licenseNumber];
    const onChange = (event: any, v: boolean) => handleAccessChange(Number(licenseNumber), v, user.account);
    return (
      <Checkbox checked={value} onChange={onChange} />
    );
  };

  const checkboxHeaderRenderer = (params: any) => {
    const licenseNumber = params.displayName;
    const license = props.contract.orderItems.find((o: any) => o.licenseNumber.toString() === licenseNumber);
    const licenseName = license?.productName || licenseNumber.toString();
    return (
      <span className='ag-theme-alpine ag-header-cell-label'>{licenseName}</span>
    );
  };

  const createTable = (licenseNumbers: number[], accesses: IUserAccesses[]) => {
    setInitialAccesses(accesses);
    setLicenseNumbers(licenseNumbers);
    const tableHeaders = licenseNumbers.map(l => {
      return (
        textHeader(l.toString(), l.toString(), HeaderSizeEnum.SMALL, { renderer: checkboxRenderer, headerRenderer: checkboxHeaderRenderer })
      );
    });
    const accountHeader = textHeader("account", t("standard.account"), HeaderSizeEnum.LARGE);
    tableHeaders.unshift(accountHeader);
    setTableHeaders(tableHeaders);
  };

  const createTableData = () => {
    const accesses = initialAccesses.map(a => {
      const user: any = {
        account: a.account
      };

      licenseNumbers.forEach(l => {
        const changesOnLicense = props.licenseAccessOrder.accessChanges.find(u => u.email === a.account && u.licenseNumber === l);
        //const changesOnLicense = props.licenseAccessOrder.users.find(u => u.email === user.account && u.licenseNumbers.includes(l));
        const hasAccess = changesOnLicense ? changesOnLicense.giveAccess : a.accessToLicenseNumbers.includes(l);
        user[l.toString()] = hasAccess;
      });
      return user;
    });
    return accesses;
  };

  const tableData = createTableData();

  useEffect(() => {
    makeRequest(`companies/${companyNumber}/action/licenseAccessForLicenses`, 'POST', changesLicenseNumbers).then((response: ILicenseAccessResponse) => {
      const userAccesses: IUserAccesses[] = response.licenseNumbersWithLicensing.length > 0 ? response.accounts.map(u => {
        return {
          accessToLicenseNumbers: u.accessToLicenseNumbers,
          account: u.account,
        };
      }) : [];
      createTable(response.licenseNumbersWithLicensing, userAccesses);
    });
  }, []);

  return (
    <WebkuaTable
      headers={CreateHeaders(tableHeaders)}
      data={tableData}
    />
  );
}

export default ContractSaleDialog;